"use client";

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { usePathname, useRouter } from "next/navigation";

import {
  PasswordResetEmailContextType,
  PasswordResetUser,
} from "@/types/passwordReset";
import { RecaptchaResponse } from "@/types/recaptcha";
import { UserEmail } from "@/types/user";

import { httpRequest } from "@/lib/http";

import { usePasswordReset } from "@/context/passwordReset";
import { useRecaptcha } from "@/context/util/recaptcha";
import { useSecuritySettings } from "@/context/util/securitySettings";
import { useUi } from "@/context/util/ui";

import passwordResetAlerts from "@/components/alerts/passwordReset";
import CountdownTimer from "@/components/countdown";

const EmailContext = createContext<PasswordResetEmailContextType>(null!);

export function EmailProvider({ children }: { children: ReactNode }) {
  const { handleSetAlert, setLoading } = useUi();
  const { recaptchaRequest } = useRecaptcha();
  const { getSecurityQuestions, timer, timerSpan } = useSecuritySettings();
  const {
    activeStep,
    passwordResetUser,
    setActiveStep,
    setPasswordResetUser,
    showMissingQuestion,
  } = usePasswordReset();
  const router = useRouter();
  const pathname = usePathname();

  const [showForgotAnswer, setShowForgotAnswer] = useState<boolean>(false);

  useEffect(() => {
    let newAlert: ReactNode | null = null;

    if (activeStep === 1) {
      if (!showForgotAnswer && !showMissingQuestion)
        newAlert = passwordResetAlerts.getLookupSuccessAlert(
          passwordResetUser?.userIndicator ?? "EM",
        );
      else if (passwordResetUser?.emails?.length)
        newAlert = passwordResetAlerts.getUserEmailsSuccessAlert(
          passwordResetUser?.userIndicator ?? "EM",
          showMissingQuestion,
        );
      else if (showMissingQuestion) getUserEmails();
      else
        newAlert = passwordResetAlerts.getUserEmailsErrorAlert(
          passwordResetUser?.userIndicator ?? "EM",
        );

      handleSetAlert([1], newAlert);
    }
  }, [
    activeStep,
    passwordResetUser?.emails,
    passwordResetUser?.userIndicator,
    showForgotAnswer,
    showMissingQuestion,
  ]);

  useEffect(() => {
    const timerElement = timerSpan.current;
    if (!timerElement) return;

    if (timerElement.textContent === "0 minutes 0 seconds") {
      const userIndicator = passwordResetUser?.userIndicator ?? "EM";

      // show expired token no matter where the user is in the process
      handleSetAlert(
        [0, 1, 2],
        passwordResetAlerts.getExpiredTokenAlert(userIndicator),
      );

      setActiveStep(1);

      timer.current = null;
      timerSpan.current = null;
    }
  }, [passwordResetUser?.userIndicator, passwordResetUser?.token]);

  const getUserEmails = useCallback(() => {
    const request = async (init?: RequestInit) => {
      window.scrollTo(0, 0);
      setLoading(true);

      try {
        const url = `/account-utilities/api/password-reset/email?username=${passwordResetUser?.username}`;

        const requestInit: RequestInit = {
          headers: { ...(init?.headers ?? {}) },
          method: "GET",
        };

        const emails = (
          await httpRequest<UserEmail[] | RecaptchaResponse>(url, requestInit)
        ).data;

        if (!emails) throw new Error("Empty http response");

        if ("valid" in emails) {
          handleSetAlert([0], passwordResetAlerts.getRecaptchaAlert());
          return emails;
        }

        setPasswordResetUser((prev) => (prev ? { ...prev, emails } : null));

        setShowForgotAnswer(true);
      } catch (error) {
        const message = "Failed to get user emails. Please try again later.";
        console.error(message, error);
      } finally {
        setLoading(false);
      }
    };

    return recaptchaRequest("get_emails", request);
  }, [passwordResetUser?.username, recaptchaRequest]);

  const validateResetToken = useCallback(
    (token: string) => {
      const request = async (init?: RequestInit) => {
        window.scrollTo(0, 0);
        setLoading(true);

        let recaptchaValid = true;

        try {
          const url = `/account-utilities/api/password-reset/validate?token=${token}`;

          const requestInit: RequestInit = {
            headers: { ...(init?.headers ?? {}) },
            method: "GET",
          };

          const passwordResetData = (
            await httpRequest<PasswordResetUser | RecaptchaResponse>(
              url,
              requestInit,
            )
          ).data;

          if (!passwordResetData) throw new Error("Empty http response");

          if ("valid" in passwordResetData) {
            recaptchaValid = false;

            handleSetAlert([0], passwordResetAlerts.getRecaptchaAlert());
            return passwordResetData;
          }

          setPasswordResetUser((prev) => ({
            ...(prev ?? {}),
            ...passwordResetData,
            verified: true,
          }));

          timer.current = (
            <CountdownTimer<PasswordResetUser>
              fields={["token"]}
              ref={timerSpan}
              setExpired={setPasswordResetUser}
              targetDate={passwordResetData.tokenExpiration}
            />
          );

          handleSetAlert(
            [2],
            passwordResetAlerts.getVerificationSuccessAlert(
              passwordResetUser?.userIndicator ?? "EM",
              timer.current,
            ),
          );

          getSecurityQuestions();
          setActiveStep(2);
        } catch (error) {
          const message = "Failed to validate password reset token";
          console.error(message, error);

          handleSetAlert(
            [0],
            passwordResetAlerts.getTokenValidationErrorAlert(
              message,
              error,
              passwordResetUser?.userIndicator ?? "EM",
              timer.current,
            ),
          );
        } finally {
          if (recaptchaValid) router.push(pathname);
          else setActiveStep(0);
          setLoading(false);
        }
      };

      return recaptchaRequest("validate_reset_token", request);
    },
    [pathname, recaptchaRequest, router],
  );

  return (
    <EmailContext.Provider
      value={{
        getUserEmails,
        setShowForgotAnswer,
        showForgotAnswer,
        validateResetToken,
      }}
    >
      {children}
    </EmailContext.Provider>
  );
}

export function useEmailApi() {
  const ctx = useContext(EmailContext);
  if (!ctx) throw new Error("useEmailApi must be used within a EmailContext");
  return ctx;
}
